import { Button, FlexBox, FormError, FormField, FormInputGroup, StandardForm, TextInput } from '@vp/swan';
import React, { useContext, useState } from 'react';
import { validateEmail, validateOrderNumber } from '../../utils/validation-utils';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { CAContext } from '../../contexts/CAContext';

export interface INonLoggedInOrderFormProps {
  onSubmit?: (data: { email: string; orderNumber: string }) => void;
}

const NonLoggedInOrderForm: React.FC<INonLoggedInOrderFormProps> = ({ onSubmit }) => {
  const {
    state: { sendChatbotMessage },
  } = useContext(ChatbotContext);

  const { setChatState } = useContext(CAContext);

  const [formData, setFormData] = useState({ email: '', orderNumber: '' });
  const [errors, setErrors] = useState({ email: '', orderNumber: '' });
  const [touched, setTouched] = useState({ email: false, orderNumber: false });

  const handleBlur = (field: keyof typeof formData) => {
    setTouched({ ...touched, [field]: true });

    if (field === 'email') {
      setErrors({ ...errors, [field]: validateEmail(formData[field]) });
    }
  };

  const emailChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setChatState((prevState: any) => ({
      ...prevState,
      userInfo: {
        ...prevState.userInfo,
        userData: {
          ...(prevState.userInfo && prevState.userInfo.userData),
          email: e.target.value,
        },
      },
    }));
    setFormData({ ...formData, email: e.target.value });
  };

  const validateForm = (): boolean => {
    const newErrors = {
      email: validateEmail(formData.email),
      orderNumber: validateOrderNumber(formData.orderNumber),
    };

    setErrors(newErrors);
    setTouched({ email: true, orderNumber: true });

    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const formattedOrderNumber = formData.orderNumber.toUpperCase();

      sendChatbotMessage?.({
        type: 'non_logged_in_order_tracking',
        messageData: {
          messageText: `${formData.email}\n${formattedOrderNumber}`, // Display to user
          query: `Order ID - ${formattedOrderNumber}`,
          email: formData.email,
        }, // Send query to the chatbot
      });

      onSubmit?.({
        email: formData.email,
        orderNumber: formattedOrderNumber,
      });
    }
  };

  return (
    <StandardForm compactMode marginY="between-actions">
      <FormField>
        <FormInputGroup>
          <TextInput
            value={formData.email}
            onChange={(e) => emailChangeHandler(e)}
            onBlur={() => handleBlur('email')}
            placeholder="Email linked to your VistaPrint account"
          />
          {touched.email && errors.email && <FormError>{errors.email}</FormError>}
        </FormInputGroup>
      </FormField>
      <FormField>
        <FormInputGroup>
          <TextInput
            value={formData.orderNumber}
            onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })}
            onBlur={() => handleBlur('orderNumber')}
            placeholder="Order number"
          />
          {touched.orderNumber && errors.orderNumber && <FormError>{errors.orderNumber}</FormError>}
        </FormInputGroup>
      </FormField>
      <FlexBox justifyContent="space-around">
        <Button onClick={handleSubmit} skin="primary">
          Confirm
        </Button>
      </FlexBox>
    </StandardForm>
  );
};

export default NonLoggedInOrderForm;
